import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LOOKUP_MIN_LETTERS, lookupLetters, useClientLookup, useLookupPermitted, type LookupRow } from '../api/lookup.ts';
import { LoadFailure } from './LoadFailure.tsx';
import { StatusTag } from './StatusTag.tsx';

/**
 * WHO HOLDS A CLIENT (8 October), at the foot of the top bar's search.
 *
 * A call arrives about a client who is not yours; the protocol is to transfer
 * it to whoever holds them, and a case manager sees only their own caseload.
 * So for case managers (Social Work and Finance) and above, the search offers "Who holds a
 * client called …?", and answers with the name, reference, status and the
 * people holding each match. Nothing else: no address, no visits, no record
 * to open unless it is already yours to open.
 *
 * It runs on a press, never as you type, because every lookup is recorded
 * against the person asking. Three letters of a name; at most ten answers,
 * and "keep typing" beyond that, so it finds somebody and never lists everybody.
 */
export function ClientLookup({ text, onOpen }: { text: string; onOpen: () => void }) {
  const permitted = useLookupPermitted();
  const [asked, setAsked] = useState<string | null>(null);
  const lookup = useClientLookup(asked);

  if (permitted.data?.permitted !== true) return null;
  const enough = lookupLetters(text) >= LOOKUP_MIN_LETTERS;
  const current = asked !== null && asked === text;

  return (
    <div className="border-t border-neutral-200 bg-neutral-50">
      {!current ? (
        <button
          type="button"
          disabled={!enough}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setAsked(text)}
          className="min-h-[44px] w-full px-3 py-2 text-left text-[0.9375rem] font-semibold text-neutral-800
                     hover:bg-neutral-100 disabled:font-normal disabled:text-neutral-500"
        >
          {enough
            ? <>Who holds a client called “{text}”?</>
            : <>Who holds a client? Type at least {LOOKUP_MIN_LETTERS} letters of their name.</>}
        </button>
      ) : lookup.isError ? (
        <LoadFailure what="The lookup" verb="could not be run" error={lookup.error} forbiddenIsSession={false}
                     onRetry={() => void lookup.refetch()} retrying={lookup.isFetching} />
      ) : !lookup.data ? (
        <p className="px-3 py-3 text-[0.9375rem] text-neutral-500">Looking up…</p>
      ) : (
        <div className="py-1">
          <p className="px-3 pt-2 pb-1 text-[0.6875rem] font-bold tracking-[0.14em] text-neutral-600 uppercase">
            Who holds a client called “{asked}”
          </p>
          {lookup.data.clients.length === 0 ? (
            <p className="px-3 pb-3 text-[0.9375rem] text-neutral-600">
              No client of the agency by that name. Names and other names on record are searched, not references.
            </p>
          ) : (
            <ul className="pb-1">
              {lookup.data.clients.map((row) => <LookupLine key={row.client_ref} row={row} onOpen={onOpen} />)}
            </ul>
          )}
          {lookup.data.more && (
            <p className="px-3 pb-2 text-sm text-neutral-500">More than ten match. Type more of the name.</p>
          )}
        </div>
      )}
    </div>
  );
}

function LookupLine({ row, onOpen }: { row: LookupRow; onOpen: () => void }) {
  const holders = [
    row.case_manager ? `Case manager: ${row.case_manager}` : 'No Social Work case manager',
    row.finance_case_manager ? `Finance: ${row.finance_case_manager}` : null,
    row.covering ? `Covering: ${row.covering}` : null,
  ].filter(Boolean).join(' · ');
  const body = (
    <>
      <div className="flex min-w-0 items-baseline gap-2">
        <p className="min-w-0 truncate font-semibold text-neutral-900">{row.display_name}</p>
        <StatusTag status={row.legal_status} />
        <span className="ml-auto shrink-0 font-mono text-sm text-neutral-500 tabular-nums">{row.client_ref}</span>
      </div>
      <p className="truncate text-sm text-neutral-700">{holders}</p>
    </>
  );
  return (
    <li>
      {row.client_id ? (
        <Link to={`/clients/${row.client_id}`} onClick={onOpen}
              className="block min-h-[44px] px-3 py-2 hover:bg-neutral-100">
          {body}
        </Link>
      ) : (
        <div className="min-h-[44px] px-3 py-2">{body}</div>
      )}
    </li>
  );
}
